// ./src/utils/api/EventCauseService.ts
//
// ROADMAP #204 step 4 — the causes an EVENT collects for (its Fundraising block; what a gift on the event may go to).
// Read whole (an event has at most EVENT_CAUSES_MAX), written as a whole set in its order (`eventCausesSet`, the owner).

import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

/** Mirrors eventsmagic-api `EVENT_CAUSES_MAX` (boundary/donation.ts) — the server refuses more. */
export const EVENT_CAUSES_MAX = 10;

export interface EventCause {
  id: string;
  eventId?: string | null;
  causeId: string;
  orderNo?: number | null;
  /** #204 — the event is in test mode: its own test gifts to this cause, on top of the cause's real number. */
  testRaisedAmount?: number | null;
  testDonationsCount?: number | null;
}

/** A cause's number as an event shows it: the real one plus the event's own test gifts (none unless it is in test mode). */
export const causeNumbersOnEvent = (cause: { raisedAmount?: number | null; donationsCount?: number | null }, link?: EventCause | null): { raisedAmount: number; donationsCount: number } => ({
  raisedAmount: (cause.raisedAmount ?? 0) + (link?.testRaisedAmount ?? 0),
  donationsCount: (cause.donationsCount ?? 0) + (link?.testDonationsCount ?? 0),
});

const FIELDS = `id eventId causeId orderNo testRaisedAmount testDonationsCount`;

const LIST_QUERY = gql`
  query EventCauseList($filter: EventCauseFilter, $params: PaginationAndSorting) {
    eventCauseList(filter: $filter, params: $params) {
      code
      total
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SET_MUTATION = gql`
  mutation EventCausesSet($params: EventCausesSetInput) {
    eventCausesSet(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export class EventCauseService {
  static async list(filter?: { eventId?: string[] }, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, { filter: filter || {}, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } }, { fetchPolicy: "network-only" });
  }

  /** The event's causes, in its order — whole (a picker and the blocks' options treat them as complete). */
  static async listForEvent(eventId: string): Promise<OpResult> {
    // ⓘ No sort: the server lists them in the event's order (order_no, then created).
    return listAllPages<EventCause>(EventCauseService.list, { eventId: [eventId] });
  }

  /** Replace the event's causes with `causeIds`, in that order (empty = none). Answers the event's links. */
  static async setForEvent(eventId: string, causeIds: string[]): Promise<OpResult> {
    return ApiService.executeMutation(SET_MUTATION, { params: { eventId, causeIds } });
  }
}
